/* eslint-disable no-underscore-dangle */
import { PaginationData } from '../types';

// This is the minumum information from the cache we need to determine how to
// merge results, this may not be representative of everything on the cache
// entries but we retain all fields when merging/appending
export type TestsGroupedByComponentCache = {
  pageInfo: PaginationData;
  paginatedResults: {
    component: { __ref: string };
    specCount: number;
    specs: {
      spec: { __ref: string };
      testCount: number;
      tests: {
        __ref: string;
      }[];
    }[];
  }[];
};

type SpecsGroupedByComponentCache = TestsGroupedByComponentCache['paginatedResults'][0];

type TestsGroupedBySpecCache = SpecsGroupedByComponentCache['specs'][0];

// Merge two TestsGroupedBySpecs (old, new)
// - First clone the old instance
// - Append the new instance's tests to the cloned instance's tests
// - Set the test count on the cloned instance
// - return the cloned instance
const mergeSpecs = (
  oldSpec: TestsGroupedBySpecCache,
  newSpec: TestsGroupedBySpecCache
): TestsGroupedBySpecCache => {
  const clonedSpec = structuredClone(oldSpec);
  clonedSpec.tests = [...clonedSpec.tests, ...newSpec.tests].filter(
    (test, index, tests) => index === tests.findIndex((t) => t.__ref === test.__ref)
  );

  clonedSpec.testCount = clonedSpec.tests.length;

  return clonedSpec;
};

// Merge two SpecsGroupedByComponent (old, new)
// - First clone the old instance
// - For each of the new instance's specs
//   - if the spec exists on the cloned instance, merge the specs
//   - else append the spec to the cloned instance's specs
// - Set specCount on the cloned instance
// - return the cloned instance
const mergeComponents = (
  oldComponent: SpecsGroupedByComponentCache,
  newComponent: SpecsGroupedByComponentCache
): SpecsGroupedByComponentCache => {
  const clonedComponent = structuredClone(oldComponent);
  newComponent.specs.forEach((newSpec) => {
    const matchingSpec = clonedComponent.specs.find(
      (mergedSpec) => mergedSpec.spec.__ref === newSpec.spec.__ref
    );

    if (matchingSpec) {
      const mergedSpec = mergeSpecs(matchingSpec, newSpec);
      const specIndex = clonedComponent.specs.indexOf(matchingSpec);
      clonedComponent.specs.splice(specIndex, 1, mergedSpec);
    } else {
      clonedComponent.specs.push(newSpec);
    }
  });

  clonedComponent.specCount = clonedComponent.specs.length;

  return clonedComponent;
};

// Merge two TestsGroupedByComponent instances in cache
export function mergeTestsGroupedByComponent(
  existing: TestsGroupedByComponentCache | undefined,
  incoming: TestsGroupedByComponentCache
): TestsGroupedByComponentCache {
  const mergedPaginatedResults = [...(existing?.paginatedResults || [])];

  incoming.paginatedResults.forEach((newComponent) => {
    const matchingComponent = mergedPaginatedResults.find(
      (oldComponent) => oldComponent.component.__ref === newComponent.component.__ref
    );

    if (matchingComponent) {
      const mergedComponent = mergeComponents(matchingComponent, newComponent);
      const componentIndex = mergedPaginatedResults.indexOf(matchingComponent);
      mergedPaginatedResults.splice(componentIndex, 1, mergedComponent);
    } else {
      mergedPaginatedResults.push(newComponent);
    }
  });

  const pageInfo =
    (existing?.pageInfo.page || -1) > incoming.pageInfo.page
      ? existing.pageInfo
      : incoming.pageInfo;

  return {
    pageInfo,
    paginatedResults: mergedPaginatedResults,
  };
}
